/**
 * Lease staff: heartbeat POST …/viewer-tickets/{t}/extend
 * mientras la sesión de plataforma sigue válida (CONTRATO-UDV-TICKET-LEASE).
 * Sin lease_token (paciente / share) → no-op.
 */

import { getTicketApiBase } from './viewerTicket'

export const DEFAULT_EXTEND_INTERVAL_SEC = 300
export const STAFF_LEASE_EXPIRED_EVENT = 'udv-staff-lease-expired'

/**
 * @param {object|null|undefined} data elev JSON
 * @returns {{
 *   leaseToken: string|null,
 *   leaseExpiresAt: string|null,
 *   extendIntervalSeconds: number,
 * }}
 */
export function parseLeaseFromElevation(data) {
  if (!data || typeof data !== 'object') {
    return {
      leaseToken: null,
      leaseExpiresAt: null,
      extendIntervalSeconds: DEFAULT_EXTEND_INTERVAL_SEC,
    }
  }
  const leaseTokenRaw =
    data.lease_token != null
      ? data.lease_token
      : data.leaseToken != null
        ? data.leaseToken
        : null
  const leaseToken =
    leaseTokenRaw != null && String(leaseTokenRaw).trim() !== ''
      ? String(leaseTokenRaw).trim()
      : null
  const leaseExpiresAtRaw =
    data.lease_expires_at != null
      ? data.lease_expires_at
      : data.leaseExpiresAt != null
        ? data.leaseExpiresAt
        : null
  const leaseExpiresAt =
    leaseExpiresAtRaw != null && String(leaseExpiresAtRaw).trim() !== ''
      ? String(leaseExpiresAtRaw).trim()
      : null
  const intervalRaw =
    data.extend_interval_seconds != null
      ? data.extend_interval_seconds
      : data.extendIntervalSeconds != null
        ? data.extendIntervalSeconds
        : DEFAULT_EXTEND_INTERVAL_SEC
  const n = Number(intervalRaw)
  const extendIntervalSeconds =
    Number.isFinite(n) && n >= 30 ? Math.floor(n) : DEFAULT_EXTEND_INTERVAL_SEC
  return { leaseToken, leaseExpiresAt, extendIntervalSeconds }
}

/**
 * @param {string} ticketId
 * @param {{
 *   baseUrl?: string,
 *   leaseToken: string,
 *   related?: boolean,
 *   fetchImpl?: typeof fetch,
 * }} opts
 * @returns {Promise<{
 *   ok: boolean,
 *   status?: number,
 *   reason?: string,
 *   expiresAt?: string|null,
 *   leaseExpiresAt?: string|null,
 *   leaseToken?: string|null,
 *   candidates?: object[]|null,
 * }>}
 */
export async function extendViewerTicket(ticketId, opts = {}) {
  const id = ticketId != null ? String(ticketId).trim() : ''
  const leaseToken =
    opts.leaseToken != null ? String(opts.leaseToken).trim() : ''
  if (!id || !leaseToken) {
    return { ok: false, reason: 'missing_lease' }
  }
  const base = (opts.baseUrl || getTicketApiBase()).replace(/\/+$/, '')
  const url = `${base}/api/v1/viewer-tickets/${encodeURIComponent(id)}/extend`
  const fetchFn = opts.fetchImpl || fetch
  let res
  try {
    res = await fetchFn(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${leaseToken}`,
      },
      body: JSON.stringify(
        opts.related === true ? { related: true } : {}
      ),
    })
  } catch (e) {
    return { ok: false, reason: 'network' }
  }

  let data = null
  try {
    data = await res.json()
  } catch (e) {
    /* ignore */
  }

  if (res.status === 401 || res.status === 403) {
    return {
      ok: false,
      status: res.status,
      reason: (data && (data.reason || data.error)) || 'unauthorized',
    }
  }
  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      reason: (data && (data.reason || data.error)) || 'http',
    }
  }

  const nextLease =
    data && (data.lease_token || data.leaseToken)
      ? String(data.lease_token || data.leaseToken).trim()
      : null
  return {
    ok: true,
    status: res.status,
    expiresAt:
      data && (data.expires_at || data.expiresAt)
        ? String(data.expires_at || data.expiresAt)
        : null,
    leaseExpiresAt:
      data && (data.lease_expires_at || data.leaseExpiresAt)
        ? String(data.lease_expires_at || data.leaseExpiresAt)
        : null,
    leaseToken: nextLease || leaseToken,
    candidates: data && Array.isArray(data.candidates) ? data.candidates : null,
  }
}

/**
 * @param {{
 *   ticketId: string,
 *   ticketApiBase: string,
 *   leaseToken: string,
 *   intervalSec?: number,
 *   related?: boolean,
 *   fetchImpl?: typeof fetch,
 *   onExpired?: (info: { reason?: string, status?: number }) => void,
 *   onExtended?: (result: object) => void,
 * }} opts
 * @returns {{ stop: () => void, extendOnce: () => Promise<object> }}
 */
export function startTicketLeaseHeartbeat(opts) {
  const ticketId = opts && opts.ticketId != null ? String(opts.ticketId).trim() : ''
  let leaseToken =
    opts && opts.leaseToken != null ? String(opts.leaseToken).trim() : ''
  const ticketApiBase =
    opts && opts.ticketApiBase != null
      ? String(opts.ticketApiBase).replace(/\/+$/, '')
      : getTicketApiBase()
  const intervalSec = Math.max(
    30,
    Number(opts && opts.intervalSec) || DEFAULT_EXTEND_INTERVAL_SEC
  )
  const related = !!(opts && opts.related)
  const fetchImpl = opts && opts.fetchImpl
  const onExpired = opts && typeof opts.onExpired === 'function' ? opts.onExpired : null
  const onExtended =
    opts && typeof opts.onExtended === 'function' ? opts.onExtended : null

  let stopped = false
  let timer = null
  let inFlight = false

  async function extendOnce() {
    if (stopped || !ticketId || !leaseToken) {
      return { ok: false, reason: 'stopped_or_missing' }
    }
    if (inFlight) return { ok: false, reason: 'in_flight' }
    inFlight = true
    try {
      const result = await extendViewerTicket(ticketId, {
        baseUrl: ticketApiBase,
        leaseToken,
        related,
        fetchImpl,
      })
      if (stopped) return result
      if (result.ok) {
        if (result.leaseToken) leaseToken = result.leaseToken
        if (onExtended) onExtended(result)
        return result
      }
      if (
        result.status === 401 ||
        result.status === 403 ||
        result.reason === 'unauthorized'
      ) {
        if (onExpired) onExpired(result)
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent(STAFF_LEASE_EXPIRED_EVENT, { detail: result })
            )
          }
        } catch (e) {
          /* ignore */
        }
        stop()
      }
      return result
    } finally {
      inFlight = false
    }
  }

  function schedule() {
    if (stopped || timer != null) return
    timer = setInterval(() => {
      void extendOnce()
    }, intervalSec * 1000)
  }

  function stop() {
    stopped = true
    if (timer != null) {
      clearInterval(timer)
      timer = null
    }
  }

  if (ticketId && leaseToken) {
    schedule()
    // Primer extend pronto (no esperar N min) para re-firmar manifests.
    setTimeout(() => {
      if (!stopped) void extendOnce()
    }, 2500)
  }

  return { stop, extendOnce }
}
